import React, { useState } from "react";
import { ArrowLeft, ArrowRight, CalendarCheck, Check, Sparkles } from "lucide-react";

// The two ways in. Both land in the same journey — this only decides whether the
// visitor answers three questions first.
export const HelpChoicePanel = ({ help, onStart, onSkip }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7" data-testid="pricing-help-choice">
    <div className="flex items-start gap-3">
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange/10 text-orange">
        <Sparkles size={17} />
      </span>
      <div className="min-w-0">
        <h3 className="font-display text-base font-bold text-navy md:text-lg">
          {help.heading || "Not sure which accounting services you need?"}
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
          {help.intro || "Answer three quick questions and we'll help you find the right support for your business."}
        </p>
      </div>
    </div>
    <div className="mt-5 flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={onStart}
        data-testid="pricing-help-start"
        className="inline-flex items-center gap-2 rounded-full bg-orange px-7 py-3.5 font-bold text-white duration-300 [transition-property:transform,background-color] hover:-translate-y-0.5 hover:bg-orange-dark"
      >
        {help.start_label || "Help me choose"} <ArrowRight size={16} />
      </button>
      <button
        type="button"
        onClick={onSkip}
        data-testid="pricing-help-skip"
        className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 font-bold text-navy duration-200 [transition-property:border-color,background-color] hover:border-navy"
      >
        {help.skip_label || "I'll choose my services"}
      </button>
    </div>
  </div>
);

const answered = (value) => (Array.isArray(value) ? value.length > 0 : Boolean(value));

// The three questions. One at a time, under a minute. Single-choice questions
// move on as soon as they're tapped; "what would you like help with" takes as many
// answers as apply, so it waits for Next.
export default function HelpMeChoose({ help, questions, answers, onAnswer, onDone, onBack, onDiscovery }) {
  const [index, setIndex] = useState(0);
  const q = questions[index];
  if (!q) return null;

  const value = answers[q.key];
  const isOn = (key) => (q.multi ? (value || []).includes(key) : value === key);
  // "Looking after all my business finances" covers the rest, so it ticks them all
  // and holds them: unticking any one of them on its own would contradict it.
  const masterKey = (q.options || []).find((o) => o.selects_all)?.key || "";
  const masterOn = Boolean(masterKey) && isOn(masterKey);

  const choose = (key) => {
    let next;
    if (q.multi && key === masterKey) {
      next = {
        ...answers,
        // "I'm not sure" is the opposite of asking for everything, so it stays out.
        [q.key]: masterOn ? [] : (q.options || []).filter((o) => !o.excluded_from_all).map((o) => o.key),
      };
    } else if (q.multi) {
      next = { ...answers, [q.key]: isOn(key) ? (value || []).filter((v) => v !== key) : [...(value || []), key] };
    } else {
      next = { ...answers, [q.key]: key };
    }
    onAnswer(q.key, next[q.key]);
    return next;
  };

  const advance = (next) => {
    if (index + 1 < questions.length) setIndex(index + 1);
    // The state update hasn't landed yet, so hand over the answers directly.
    else onDone(next);
  };

  const back = () => (index === 0 ? onBack() : setIndex(index - 1));

  return (
    <div className="max-w-2xl" data-testid="pricing-help-questions">
      <div className="flex items-center gap-3">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Question {index + 1} of {questions.length}
        </p>
        <div className="flex gap-1.5" aria-hidden="true">
          {questions.map((_, i) => (
            <span key={i} className={`h-1.5 w-7 rounded-full duration-300 [transition-property:background-color] ${i <= index ? "bg-orange" : "bg-slate-200"}`} />
          ))}
        </div>
      </div>

      <fieldset className="mt-5">
        <legend className="font-display text-2xl font-extrabold leading-tight text-navy md:text-3xl">{q.label}</legend>
        {(q.help || q.multi) && (
          <p className="mt-2 text-sm text-slate-500">{q.help || "Choose as many as apply."}</p>
        )}
        <div className="mt-6 space-y-2.5">
          {(q.options || []).map((o, i) => {
            const on = isOn(o.key);
            const held = masterOn && o.key !== masterKey;
            // The option that covers everything is the whole proposition, so it
            // leads the list rather than sitting as one of equals.
            const lead = Boolean(o.lead);
            const divider = lead && i + 1 < (q.options || []).length;
            return (
              <React.Fragment key={o.key}>
                <label
                  data-testid={`pricing-help-option-${q.key}-${o.key}`}
                  // A tap on a single-choice question moves straight on. Arrow keys
                  // through a radio group fire a change on every option, so a keyboard
                  // visitor browsing the list would be thrown forward — they get the
                  // Next button instead. Multi-select always waits for Next.
                  onClick={(e) => {
                    if (q.multi) return; // the checkbox's own change event handles it
                    const next = choose(o.key);
                    if (e.detail > 0) advance(next);
                  }}
                  className={`flex gap-3 rounded-xl border duration-200 [transition-property:border-color,background-color,opacity,box-shadow] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-orange/40 ${
                    held ? "cursor-default opacity-60" : "cursor-pointer"
                  } ${lead ? "items-start p-5 md:p-6" : "items-center p-4"} ${
                    on
                      ? "border-orange bg-orange/[0.07]"
                      : lead
                        ? "border-orange/40 bg-white shadow-[0_1px_0_0_rgba(15,23,42,0.03)] hover:border-orange"
                        : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  {q.multi ? (
                    <span className={`relative inline-flex h-5 w-5 shrink-0 items-center justify-center ${lead ? "mt-0.5" : ""}`}>
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={held}
                        onChange={() => choose(o.key)}
                        className="peer absolute h-5 w-5 cursor-pointer opacity-0 disabled:cursor-default"
                        aria-label={o.label}
                      />
                      <span
                        aria-hidden="true"
                        className={`inline-flex h-5 w-5 items-center justify-center rounded-md border-2 duration-200 [transition-property:background-color,border-color] ${
                          on ? "border-orange bg-orange text-white" : "border-slate-300 bg-white text-transparent"
                        }`}
                      >
                        <Check size={13} strokeWidth={3} />
                      </span>
                    </span>
                  ) : (
                    <input
                      type="radio"
                      name={q.key}
                      value={o.key}
                      checked={on}
                      onChange={() => choose(o.key)}
                      className="h-4 w-4 shrink-0 accent-orange"
                    />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                      <span className={`font-semibold text-navy ${lead ? "font-display text-base font-bold md:text-lg" : "text-sm"}`}>
                        {o.label}
                      </span>
                      {lead && o.badge && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-orange/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-orange">
                          <Sparkles size={11} /> {o.badge}
                        </span>
                      )}
                    </span>
                    {lead && o.note && <span className="mt-1.5 block text-sm leading-relaxed text-slate-600">{o.note}</span>}
                  </span>
                  {held && on && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-slate-400">Included</span>}
                </label>
                {divider && (
                  <p className="!mt-5 flex items-center gap-3 pb-1 text-xs font-bold uppercase tracking-widest text-slate-400">
                    <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
                    {help.lead_divider || "Or tell us which areas you'd like help with"}
                    <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
                  </p>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </fieldset>

      <div className="mt-7 flex flex-wrap items-center gap-5">
        <button
          type="button"
          disabled={!answered(value)}
          onClick={() => advance({ ...answers })}
          data-testid="pricing-help-next"
          className="inline-flex items-center gap-2 rounded-full bg-orange px-7 py-3.5 font-bold text-white duration-300 [transition-property:transform,background-color,opacity] hover:-translate-y-0.5 hover:bg-orange-dark disabled:cursor-not-allowed disabled:opacity-40"
        >
          {index + 1 < questions.length ? "Next question" : "See what we'd suggest"} <ArrowRight size={16} />
        </button>
        <button
          type="button"
          onClick={back}
          data-testid="pricing-help-back"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 duration-200 [transition-property:color] hover:text-navy"
        >
          <ArrowLeft size={15} /> Back
        </button>
        <button
          type="button"
          onClick={onDiscovery}
          data-testid="pricing-help-discovery"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 duration-200 [transition-property:color] hover:text-navy"
        >
          <CalendarCheck size={15} /> I'd rather just talk to someone
        </button>
      </div>
    </div>
  );
}
